import React from "react";
import { Facebook, Instagram, Linkedin, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";

const SOCIALS = [
  { icon: Facebook, href: "https://www.facebook.com/share/1AJgBbL7Uq/?mibextid=wwXIfr", label: "Facebook", testid: "social-facebook" },
  { icon: Instagram, href: "https://www.instagram.com/afrigoall", label: "Instagram", testid: "social-instagram" },
  { icon: TikTokIcon, href: "https://www.tiktok.com/@afrigoall", label: "TikTok", testid: "social-tiktok" },
  { icon: XIcon, href: "https://x.com/afrigoall", label: "X (Twitter)", testid: "social-x" },
  { icon: Linkedin, href: "https://www.linkedin.com/company/afrigoall/", label: "LinkedIn", testid: "social-linkedin" },
  { icon: WhatsAppIcon, href: "https://wa.me/18575674775", label: "WhatsApp +1 857-567-4775", testid: "social-whatsapp" },
];

// Simple inline X (Twitter) icon since lucide's Twitter is old bird logo
function XIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width={props.size || 18} height={props.size || 18}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width={props.size || 18} height={props.size || 18}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.35a8.16 8.16 0 0 0 4.77 1.52V6.42a4.85 4.85 0 0 1-1.84-.27z" />
    </svg>
  );
}

function WhatsAppIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width={props.size || 18} height={props.size || 18}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.031-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.174.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.981.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885zM20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a11.883 11.883 0 0 0 5.71 1.453h.006c6.585 0 11.946-5.336 11.949-11.893a11.821 11.821 0 0 0-3.481-8.409z" />
    </svg>
  );
}

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-white/10 bg-black mt-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-8">
        <div>
          <img
            src="/logo.png"
            alt="AfriGoall"
            className="h-14 w-auto"
            data-testid="footer-logo-img"
          />
          <p className="mt-4 text-sm text-white/60 max-w-xs">
            {t("footer.tagline")}
          </p>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b] mb-4">{t("contact.title")}</div>
          <a
            href="mailto:info@afrigoall.com"
            data-testid="footer-email-link"
            className="flex items-center gap-2 text-sm text-white hover:text-[#e65f2b] transition-colors"
          >
            <Mail size={16} />
            info@afrigoall.com
          </a>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b] mb-4">{t("footer.company")}</div>
          <div className="flex flex-wrap gap-3">
            {SOCIALS.map(({ icon: Icon, href, label, testid }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={testid}
                className="w-10 h-10 border border-white/15 flex items-center justify-center hover:border-[#e65f2b] hover:text-[#e65f2b] hover:bg-[#e65f2b]/5 transition-colors text-white/80"
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 text-xs text-white/40 flex flex-wrap justify-between gap-2">
          <div>{t("footer.rights", { year: new Date().getFullYear() })}</div>
        </div>
      </div>
    </footer>
  );
}
